import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "./config";
import "./css/SraImportPage.css";

// Generic public-data import page (SRA / GDC / 1000 Genomes), driven by a
// per-source config (publicDataConfigs.js).
//   - Flat mode (SRA, GDC): each entered ID is one file; popup lists them.
//   - Expand mode (1000G): each entered SAMPLE expands to many files; popup is a
//     grouped, collapsible picker (sample → collection → source → file).
const PublicDataImportPage = ({ config }) => {
  const { source, title, itemWord, idHelp, requestsPath, localValidate, docsUrl, docsLabel } = config;

  const [existingProjects, setExistingProjects] = useState([]);
  const [idText, setIdText] = useState("");
  const [newProject, setNewProject] = useState("");
  const [selectedProject, setSelectedProject] = useState("");

  const [errorMsg, setErrorMsg] = useState("");
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState(null);

  const [redownload, setRedownload] = useState(() => new Set()); // flat mode: opt-in re-download
  const [picked, setPicked] = useState(() => new Set());         // expand mode: chosen files
  const [collapsed, setCollapsed] = useState(() => new Set());   // expand mode: collapsed samples

  const navigate = useNavigate();
  const isExpand = !!(validation && validation.expand);

  const toggleIn = (setter) => (key) =>
    setter((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleRedownload = toggleIn(setRedownload);
  const togglePick = toggleIn(setPicked);
  const toggleCollapsed = toggleIn(setCollapsed);

  useEffect(() => {
    const load = async () => {
      const session = await fetch("/api/auth/session", { method: "GET", credentials: "include" }).then((r) => r.json());
      if (!session.authenticated) return;
      const res = await axios.post(`${API_BASE}/user-files`, { cognitoUserId: session.cognitoUserId });
      const projects = Array.from(new Set((res.data.files || []).map((f) => f.original_project))).filter(Boolean);
      setExistingProjects(projects);
    };
    load();
  }, []);

  const parsedIds = () => idText.split("\n").map((x) => x.trim()).filter(Boolean);

  const validateLocalAll = () => {
    const ids = parsedIds();
    if (ids.some((line) => line.includes(",") || line.includes(" "))) return "Each ID must be on its own line with no commas or spaces.";
    if (ids.length === 0) return "Please enter at least one ID.";
    if (ids.length > 50) return "You may enter a maximum of 50 IDs.";
    const fmt = localValidate(ids);
    if (fmt) return fmt;
    if (new Set(ids).size !== ids.length) return "Duplicate IDs are not allowed.";
    if (newProject) {
      if (!/^[A-Za-z0-9_]+$/.test(newProject)) return "Project name may only contain letters, numbers, and underscores.";
      if (existingProjects.includes(newProject)) return "A project with this name already exists.";
    }
    if (newProject && selectedProject) return "Choose either a new project name OR an existing project, not both.";
    if (!newProject && !selectedProject) return "Please choose a project name (new or existing).";
    return null;
  };

  const handleImport = async () => {
    const err = validateLocalAll();
    if (err) { setErrorMsg(err); return; }
    setValidating(true);
    try {
      const project = newProject || selectedProject;
      const res = await axios.post(`${API_BASE}/api/public-data/validate`, { source, ids: parsedIds() }, { withCredentials: true });
      setRedownload(new Set());
      setPicked(new Set());
      setCollapsed(new Set());
      setValidation({ ...res.data, project });
    } catch (e) {
      setErrorMsg(e.response?.data?.error || "Validation failed. Please try again.");
    } finally {
      setValidating(false);
    }
  };

  const selectedItems = () => {
    if (!validation) return [];
    if (validation.expand) return validation.items.filter((i) => picked.has(i.id));
    return validation.items.filter((i) => i.status === "ok" || (i.status === "already_downloaded" && redownload.has(i.id)));
  };

  const confirmImport = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/public-data/import`,
        { source, projectName: validation.project, ids: selectedItems().map((i) => i.id) },
        { withCredentials: true }
      );
      setValidation(null);
      navigate("/downloads", { state: { message: "Your download request has been submitted." } });
    } catch (e) {
      setErrorMsg(e.response?.data?.error || "Failed to start download.");
      setValidation(null);
    } finally {
      setSubmitting(false);
    }
  };

  const fmtGB = (bytes) => (Number(bytes || 0) / 1024 ** 3).toFixed(3) + " GB";
  const perFileCredits = (bytes) =>
    validation ? Math.max(1, Math.round(((bytes || 0) / 1024 ** 3) * validation.creditsPerGb * 100) / 100) : 0;

  const sel = selectedItems();
  const selCount = sel.length;
  const selBytes = sel.reduce((a, b) => a + (b.bytes || 0), 0);
  const selCredits = Math.round(sel.reduce((a, b) => a + perFileCredits(b.bytes), 0) * 100) / 100;

  // ---- expand-mode grouping: sample -> collection -> source -> [files] ----
  const buildGroups = () => {
    const bySample = {};
    const notFound = [];
    for (const it of validation.items) {
      if (it.status === "not_found" || it.status === "not_public") { notFound.push(it); continue; }
      const s = it.sample || "?";
      bySample[s] = bySample[s] || {};
      bySample[s][it.collection] = bySample[s][it.collection] || {};
      const src = it.source || "root";
      (bySample[s][it.collection][src] = bySample[s][it.collection][src] || []).push(it);
    }
    return { bySample, notFound };
  };

  const setSamplePicked = (fileItems, on) =>
    setPicked((prev) => {
      const n = new Set(prev);
      fileItems.forEach((f) => (on ? n.add(f.id) : n.delete(f.id)));
      return n;
    });

  return (
    <div className="sra-container">
      <h1>{title}</h1>

      <label>{idHelp}</label>
      {docsUrl && (
        <div style={{ margin: "4px 0 8px", fontSize: 13 }}>
          <a href={docsUrl} target="_blank" rel="noopener noreferrer">{docsLabel || "Documentation"} ↗</a>
        </div>
      )}
      <textarea className="sra-textarea" value={idText} onChange={(e) => setIdText(e.target.value)} />

      <div className="sra-project-section">
        <h3>Project Name</h3>
        <label>Enter New Project</label>
        <input type="text" className="sra-input" value={newProject} onChange={(e) => setNewProject(e.target.value)} />
        <div className="sra-divider">OR</div>
        <label>Select Existing Project</label>
        <select className="sra-select" value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
          <option value="">-- Select --</option>
          {existingProjects.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="sra-button-row">
        <button className="sra-import-btn" onClick={handleImport} disabled={validating}>
          {validating ? "Looking up files…" : `Find ${itemWord}s`}
        </button>
      </div>

      {errorMsg && (
        <Modal title="Validation Error" onClose={() => setErrorMsg("")}><p>{errorMsg}</p></Modal>
      )}

      {validation && (
        <Modal title="Select files to download" onClose={() => !submitting && setValidation(null)}>
          {isExpand ? (
            <ExpandPicker
              groups={buildGroups()}
              picked={picked}
              collapsed={collapsed}
              togglePick={togglePick}
              toggleCollapsed={toggleCollapsed}
              setSamplePicked={setSamplePicked}
              fmtGB={fmtGB}
            />
          ) : (
            <FlatList
              items={validation.items}
              redownload={redownload}
              toggleRedownload={toggleRedownload}
              fmtGB={fmtGB}
            />
          )}

          <div style={{ marginTop: 12, fontSize: 14, borderTop: "1px solid #e5e7eb", paddingTop: 10 }}>
            <div><strong>{selCount}</strong> {itemWord}(s) selected.</div>
            <div>Estimated size: <strong>{fmtGB(selBytes)}</strong></div>
            <div>
              Estimated cost: <strong>{selCredits} credits</strong>{" "}
              <span style={{ color: "#6b7280" }}>({validation.creditsPerGb} credit/GB, min 1/file; billed on actual size)</span>
            </div>
            {validation.balance != null && <div>Your balance: <strong>{validation.balance}</strong> credits</div>}
          </div>

          <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setValidation(null)} disabled={submitting}>Cancel</button>
            <button
              className="sra-confirm-btn"
              onClick={confirmImport}
              disabled={submitting || selCount === 0}
              title={selCount === 0 ? `Select at least one ${itemWord} to download.` : undefined}
            >
              {submitting ? "Submitting…" : `Download ${selCount} ${itemWord}(s)`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ---- Flat list (SRA / GDC): one row per entered ID ----
const FlatList = ({ items, redownload, toggleRedownload, fmtGB }) => {
  const badge = (it) => {
    if (it.status === "ok") return <span style={{ color: "#166534", fontWeight: 600 }}>✅ Will download</span>;
    if (it.status === "already_downloaded") return <span style={{ color: "#b45309", fontWeight: 600 }}>⚠️ Already downloaded</span>;
    return <span style={{ color: "#b91c1c", fontWeight: 600 }}>⛔ Not available</span>;
  };
  return (
    <div style={{ maxHeight: "45vh", overflowY: "auto", marginTop: 8, border: "1px solid #e5e7eb", borderRadius: 6 }}>
      <table className="sra-validation-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead><tr>
          <th style={{ textAlign: "left", padding: "4px 6px" }}>ID</th>
          <th style={{ textAlign: "left", padding: "4px 6px" }}>Filename</th>
          <th style={{ textAlign: "left", padding: "4px 6px" }}>Status</th>
          <th style={{ textAlign: "right", padding: "4px 6px" }}>Est. size</th>
        </tr></thead>
        <tbody>
          {items.map((it) => {
            const included = it.status === "ok" || (it.status === "already_downloaded" && redownload.has(it.id));
            return (
              <tr key={it.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                <td style={{ padding: "4px 6px", fontFamily: "monospace", wordBreak: "break-all" }}>{it.id}</td>
                <td style={{ padding: "4px 6px", wordBreak: "break-all" }}>{it.file_name || "—"}</td>
                <td style={{ padding: "4px 6px" }}>
                  {badge(it)}
                  {it.reason && <div style={{ fontSize: 12, color: "#6b7280" }}>{it.reason}</div>}
                  {it.status === "already_downloaded" && (
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, marginTop: 4, cursor: "pointer" }}>
                      <input type="checkbox" checked={redownload.has(it.id)} onChange={() => toggleRedownload(it.id)} /> Download again
                    </label>
                  )}
                </td>
                <td style={{ padding: "4px 6px", textAlign: "right" }}>{included ? (it.bytes > 0 ? fmtGB(it.bytes) : "unknown") : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---- Grouped picker (1000 Genomes): sample -> collection -> source -> files ----
const ExpandPicker = ({ groups, picked, collapsed, togglePick, toggleCollapsed, setSamplePicked, fmtGB }) => {
  const { bySample, notFound } = groups;
  const samples = Object.keys(bySample);
  return (
    <div style={{ maxHeight: "55vh", overflowY: "auto", marginTop: 8 }}>
      {notFound.length > 0 && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "6px 10px", marginBottom: 8, fontSize: 13, color: "#b91c1c" }}>
          {notFound.map((n) => <div key={n.id}>⛔ {n.id}: {n.reason}</div>)}
        </div>
      )}
      {samples.map((s) => {
        const allFiles = Object.values(bySample[s]).flatMap((byShrc) => Object.values(byShrc).flat());
        const isCollapsed = collapsed.has(s);
        return (
          <div key={s} style={{ border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: "#f8fafc", cursor: "pointer" }}
                 onClick={() => toggleCollapsed(s)}>
              <strong>{isCollapsed ? "▸" : "▾"} {s}</strong>
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                {allFiles.length} files{" · "}
                <button style={{ marginLeft: 6 }} onClick={(e) => { e.stopPropagation(); setSamplePicked(allFiles, true); }}>Select all</button>{" "}
                <button onClick={(e) => { e.stopPropagation(); setSamplePicked(allFiles, false); }}>Clear</button>
              </span>
            </div>
            {!isCollapsed && (
              <table className="sra-validation-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr>
                  <th style={{ width: 24 }}></th>
                  <th style={{ textAlign: "left", padding: "3px 6px" }}>Phase</th>
                  <th style={{ textAlign: "left", padding: "3px 6px" }}>Source</th>
                  <th style={{ textAlign: "left", padding: "3px 6px" }}>File</th>
                  <th style={{ textAlign: "left", padding: "3px 6px" }}>Type</th>
                  <th style={{ textAlign: "right", padding: "3px 6px" }}>Size</th>
                </tr></thead>
                <tbody>
                  {Object.keys(bySample[s]).sort().flatMap((coll) =>
                    Object.keys(bySample[s][coll]).sort().flatMap((src) =>
                      bySample[s][coll][src].map((it) => (
                        <tr key={it.id} style={{ borderTop: "1px solid #f1f5f9", background: picked.has(it.id) ? "#f0fdf4" : undefined }}>
                          <td style={{ textAlign: "center" }}>
                            <input type="checkbox" checked={picked.has(it.id)} onChange={() => togglePick(it.id)} />
                          </td>
                          <td style={{ padding: "3px 6px" }}>{coll}</td>
                          <td style={{ padding: "3px 6px" }}>{src}</td>
                          <td style={{ padding: "3px 6px", wordBreak: "break-all" }}>
                            {it.file_name}
                            {it.status === "already_downloaded" && <span style={{ color: "#b45309", fontSize: 11 }}> ⚠️ already downloaded</span>}
                          </td>
                          <td style={{ padding: "3px 6px" }}>{it.file_type}</td>
                          <td style={{ padding: "3px 6px", textAlign: "right", whiteSpace: "nowrap" }}>{it.bytes > 0 ? fmtGB(it.bytes) : "—"}</td>
                        </tr>
                      ))
                    )
                  )}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Modal = ({ title, children, onClose }) => (
  <div className="sra-modal-backdrop">
    <div className="sra-modal-box">
      <h2>{title}</h2>
      <div className="sra-modal-content">{children}</div>
      <button className="sra-modal-close" onClick={onClose}>Close</button>
    </div>
  </div>
);

export default PublicDataImportPage;
